import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/auth.store';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

/** Formato de erro devolvido pela API (AllExceptionsFilter + ZodValidationPipe). */
export interface ApiFieldError {
  path: string;
  message: string;
  code?: string;
}

export interface ApiErrorBody {
  message?: string | string[];
  errors?: ApiFieldError[];
  statusCode?: number;
  path?: string;
  timestamp?: string;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean; _skipAuth?: boolean };

/**
 * Teto de tempo para chamadas que esperam a IA responder.
 *
 * Existe porque o timeout padrão de 60 s cancelava TODA geração de imagem: o
 * modelo leva ~1 min por peça e o pedido de 4 variações passa de três minutos.
 * O navegador abortava antes da API responder e a tela mostrava "a requisição
 * demorou demais" com a imagem já sendo cobrada da OpenAI do outro lado.
 *
 * A cadeia de tempos precisa ficar em ordem crescente do cliente para fora:
 * navegador (300 s) ≥ nginx `proxy_read_timeout` (300 s) ≥ OPENAI_TIMEOUT_MS
 * (120 s) × tentativas. Se o navegador for o mais curto, ele cancela trabalho
 * que já estava pago.
 */
export const AI_TIMEOUT_MS = 300_000;

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60_000,
});

api.interceptors.request.use((config: RetriableConfig) => {
  if (!config._skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/* ------------------------------------------------------------------ *
 * Refresh de token: uma única tentativa, com fila de requisições pendentes.
 * ------------------------------------------------------------------ */

let refreshInFlight: Promise<string> | null = null;
const waiters: Array<(token: string | null) => void> = [];

function resolveWaiters(token: string | null): void {
  while (waiters.length) waiters.shift()?.(token);
}

async function performRefresh(): Promise<string> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('Sessão expirada');

  const { data } = await axios.post<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }>(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { headers: { 'Content-Type': 'application/json' } });

  useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
  return data.accessToken;
}

export function forceLogout(): void {
  useAuthStore.getState().clear();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}`);
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    const isAuthRoute = Boolean(config?.url && /\/auth\/(login|refresh|logout)$/.test(config.url));

    if (status !== 401 || !config || config._retried || isAuthRoute) {
      return Promise.reject(error);
    }

    config._retried = true;

    if (!refreshInFlight) {
      refreshInFlight = performRefresh()
        .then((token) => {
          resolveWaiters(token);
          return token;
        })
        .catch((refreshError) => {
          resolveWaiters(null);
          forceLogout();
          throw refreshError;
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }

    const token = await new Promise<string | null>((resolve) => {
      waiters.push(resolve);
      void refreshInFlight?.catch(() => undefined);
    });

    if (!token) return Promise.reject(error);

    config.headers.set('Authorization', `Bearer ${token}`);
    return api.request(config);
  },
);

/* ------------------------------------------------------------------ *
 * Helpers tipados
 * ------------------------------------------------------------------ */

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await api.get<T>(url, config);
  return data;
}

/**
 * Busca uma lista aceitando as DUAS formas que a API usa: array puro
 * (`/clients/:id/plans`) e envelope paginado (`/campaigns`, `/leads`…).
 *
 * Existe porque tratar um envelope como array quebra a tela inteira com
 * "x.map is not a function" — foi o que derrubou /campanhas em produção. Com
 * este helper, mudar um endpoint de array para paginado (ou o contrário)
 * deixa de ser uma mudança capaz de quebrar o painel.
 */
export async function apiGetList<T>(url: string, config?: AxiosRequestConfig): Promise<T[]> {
  const data = await apiGet<T[] | { items?: T[] } | null>(url, config);
  if (Array.isArray(data)) return data;
  if (data && Array.isArray((data as { items?: T[] }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await api.post<T>(url, body, config);
  return data;
}

export async function apiPatch<T, B = unknown>(url: string, body?: B): Promise<T> {
  const { data } = await api.patch<T>(url, body);
  return data;
}

export async function apiPut<T, B = unknown>(url: string, body?: B): Promise<T> {
  const { data } = await api.put<T>(url, body);
  return data;
}

export async function apiDelete<T = void>(url: string): Promise<T> {
  const { data } = await api.delete<T>(url);
  return data;
}

/** Mensagem legível para exibir em toasts e blocos de erro. */
export function getErrorMessage(error: unknown, fallback = 'Não foi possível concluir a operação'): string {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    const body = error.response?.data;
    if (body?.message) {
      return Array.isArray(body.message) ? body.message.join(', ') : body.message;
    }
    if (error.code === 'ECONNABORTED') return 'A requisição demorou demais e foi cancelada';
    if (!error.response) return 'Sem conexão com o servidor';
    if (error.response.status === 403) return 'Você não tem permissão para esta ação';
    if (error.response.status === 404) return 'Registro não encontrado';
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function getFieldErrors(error: unknown): ApiFieldError[] {
  if (axios.isAxiosError<ApiErrorBody>(error)) return error.response?.data?.errors ?? [];
  return [];
}
